import React, { useState, useEffect } from 'react';
import { 
  Grid, 
  MapPin, 
  CheckCircle2, 
  Clock, 
  AlertTriangle, 
  CircleDot, 
  Zap, 
  Layers,
  ChevronRight,
  TrendingUp,
  X
} from 'lucide-react';
import { BuildingBlock, Floor, Zone } from '../../types';

export const ChessboardModule: React.FC = () => {
  const [matrixData, setMatrixData] = useState<{
    blocks: BuildingBlock[];
    floors: Floor[];
    zones: Zone[];
    stats: any;
  } | null>(null);
  const [loading, setLoading] = useState(true);
  const [selectedBlockId, setSelectedBlockId] = useState<string>('block-a');
  const [selectedZone, setSelectedZone] = useState<Zone | null>(null);

  const fetchMatrix = () => {
    setLoading(true);
    fetch('/api/v1/chessboard/matrix')
      .then(r => r.json())
      .then(d => {
        if (d.success) {
          setMatrixData(d.data);
          if (d.data.blocks.length > 0) setSelectedBlockId(d.data.blocks[0].id);
        }
      })
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    fetchMatrix();
  }, []);

  const getStatusColor = (status: string, pct: number) => {
    if (status === 'done' || pct >= 100) return 'bg-emerald-50 text-emerald-800 border-emerald-300 hover:bg-emerald-100 shadow-sm';
    if (status === 'in_progress' || pct > 0) return 'bg-amber-50 text-amber-900 border-amber-300 hover:bg-amber-100 shadow-sm';
    if (status === 'issue') return 'bg-rose-50 text-rose-900 border-rose-300 hover:bg-rose-100 shadow-sm';
    return 'bg-slate-50 text-slate-600 border-slate-200 hover:bg-slate-100';
  };

  const getStatusIcon = (status: string, pct: number) => {
    if (status === 'done' || pct >= 100) return <CheckCircle2 className="w-3.5 h-3.5 text-emerald-600 shrink-0" />;
    if (status === 'in_progress' || pct > 0) return <Clock className="w-3.5 h-3.5 text-amber-600 shrink-0" />;
    if (status === 'issue') return <AlertTriangle className="w-3.5 h-3.5 text-rose-600 shrink-0" />;
    return <CircleDot className="w-3.5 h-3.5 text-slate-400 shrink-0" />;
  };

  const currentBlockFloors = matrixData?.floors.filter(f => f.blockId === selectedBlockId) || [];
  const currentBlockZones = matrixData?.zones.filter(z => z.blockId === selectedBlockId) || [];

  return (
    <div className="space-y-6">
      {/* Header & Stats Overview */}
      <div className="grid grid-cols-1 sm:grid-cols-4 gap-3">
        <div className="bg-white border border-slate-200/90 p-4 rounded-2xl shadow-[0_4px_20px_-4px_rgba(0,0,0,0.05)]">
          <span className="text-xs text-slate-500 block font-semibold">Всего захваток ЭОМ</span>
          <span className="text-xl font-black text-slate-900">{matrixData?.stats.total_zones || 0}</span>
        </div>

        <div className="bg-emerald-50 border border-emerald-200 p-4 rounded-2xl shadow-sm">
          <span className="text-xs text-emerald-800 block font-bold">Готово на 100%</span>
          <span className="text-xl font-black text-emerald-900">{matrixData?.stats.done_zones || 0}</span>
        </div>

        <div className="bg-amber-50 border border-amber-200 p-4 rounded-2xl shadow-sm">
          <span className="text-xs text-amber-800 block font-bold">В процессе монтажа</span>
          <span className="text-xl font-black text-amber-900">{matrixData?.stats.in_progress_zones || 0}</span>
        </div>

        <div className="bg-purple-50 border border-purple-200 p-4 rounded-2xl shadow-sm">
          <span className="text-xs text-purple-800 block font-bold">Общая готовность объекта</span>
          <span className="text-xl font-black text-purple-900">
            {matrixData?.stats.overall_progress ? `${Math.round(matrixData.stats.overall_progress)}%` : '42%'}
          </span>
        </div>
      </div>

      {/* Main Chessboard View */}
      <div className="bg-white border border-slate-200/90 rounded-2xl p-4 sm:p-6 shadow-[0_4px_20px_-4px_rgba(0,0,0,0.05)] space-y-5">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 border-b border-slate-100 pb-4">
          <div>
            <h2 className="text-base font-bold text-slate-900 flex items-center gap-2">
              <span className="p-2 bg-purple-50 text-purple-700 border border-purple-200 rounded-xl">
                <Grid className="w-5 h-5" />
              </span>
              <span>2D Интерактивная «Шахматка» электромонтажа</span>
            </h2>
            <p className="text-xs text-slate-500 mt-1">
              Пространственная матрица стояков, МОП и щитовых помещений ЖК «Северная Звезда»
            </p>
          </div>

          {/* Block Switcher */}
          <div className="flex items-center gap-2 bg-slate-50 p-1.5 rounded-xl border border-slate-200">
            {matrixData?.blocks.map((b) => (
              <button
                key={b.id}
                onClick={() => setSelectedBlockId(b.id)}
                className={`px-3 py-1.5 rounded-lg text-xs font-bold transition active:scale-95 ${
                  selectedBlockId === b.id
                    ? 'bg-purple-600 text-white shadow-sm font-extrabold'
                    : 'text-slate-600 hover:text-slate-900 hover:bg-slate-200/60'
                }`}
              >
                {b.name}
              </button>
            ))}
          </div>
        </div>

        {/* Legend */}
        <div className="flex flex-wrap items-center gap-4 text-xs font-semibold text-slate-600 bg-slate-50 p-3 rounded-xl border border-slate-200">
          <span className="text-slate-400 font-bold">Обозначения:</span>
          <div className="flex items-center gap-1.5">
            <span className="w-3 h-3 rounded-full bg-emerald-500"></span>
            <span>Завершено & Акты АОСР (100%)</span>
          </div>
          <div className="flex items-center gap-1.5">
            <span className="w-3 h-3 rounded-full bg-amber-500"></span>
            <span>В работе прорабов</span>
          </div>
          <div className="flex items-center gap-1.5">
            <span className="w-3 h-3 rounded-full bg-rose-500 animate-pulse"></span>
            <span>Инцидент / Простой</span>
          </div>
          <div className="flex items-center gap-1.5">
            <span className="w-3 h-3 rounded-full bg-slate-300"></span>
            <span>Не начато</span>
          </div>
        </div>

        {/* Matrix Grid Representation */}
        {loading ? (
          <div className="p-8 text-center text-slate-400 text-sm">Загрузка пространственной матрицы...</div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {currentBlockZones.map((z) => {
              const pct = z.completionPercentage || 0;
              const colorClass = getStatusColor(z.status, pct);
              const icon = getStatusIcon(z.status, pct);

              return (
                <div
                  key={z.id}
                  onClick={() => setSelectedZone(z)}
                  className={`p-3.5 rounded-2xl border cursor-pointer transition ${colorClass}`}
                >
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex items-center gap-2">
                      {icon}
                      <h4 className="font-extrabold text-sm">{z.name}</h4>
                    </div>
                    <span className="font-mono text-xs font-black">{pct}%</span>
                  </div>

                  <div className="mt-2 space-y-1 text-xs opacity-90">
                    <div className="flex justify-between items-center text-[11px]">
                      <span>Отметка этажа:</span>
                      <span className="font-bold">{z.floor_name || (z as any).floorName || 'Этаж'}</span>
                    </div>
                    <div className="w-full bg-slate-200/80 h-1.5 rounded-full overflow-hidden mt-1">
                      <div
                        className={`h-full ${pct >= 100 ? 'bg-emerald-600' : 'bg-amber-600'}`}
                        style={{ width: `${pct}%` }}
                      />
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Zone Detail Modal */}
      {selectedZone && (
        <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-white border border-slate-200 rounded-3xl p-6 max-w-md w-full space-y-4 shadow-2xl animate-fade-in">
            <div className="flex items-center justify-between border-b border-slate-100 pb-3">
              <h3 className="text-base font-bold text-slate-900 flex items-center gap-2">
                <MapPin className="w-5 h-5 text-purple-600" />
                <span>Детализация зоны: {selectedZone.name}</span>
              </h3>
              <button onClick={() => setSelectedZone(null)} className="p-1.5 text-slate-400 hover:text-slate-600 rounded-full hover:bg-slate-100 transition">
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="space-y-3 text-xs text-slate-700">
              <div className="bg-slate-50 p-3 rounded-xl border border-slate-200 flex justify-between items-center">
                <span>Прогресс готовности:</span>
                <span className="font-black text-purple-700 text-sm">{selectedZone.completionPercentage || 0}%</span>
              </div>

              <div className="bg-slate-50 p-3 rounded-xl border border-slate-200 space-y-1">
                <span className="text-slate-500 block">Назначенная бригада:</span>
                <span className="font-bold text-slate-900">Бригада #1 (Иванов И.И.)</span>
              </div>

              <div className="bg-slate-50 p-3 rounded-xl border border-slate-200 space-y-1">
                <span className="text-slate-500 block">Статус АОСР:</span>
                <span className="font-bold text-emerald-700">Готово к оформлению АОСР</span>
              </div>
            </div>

            <button
              onClick={() => setSelectedZone(null)}
              className="w-full py-2.5 bg-slate-100 hover:bg-slate-200 text-slate-800 font-bold rounded-xl border border-slate-200 transition text-xs"
            >
              Закрыть
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default ChessboardModule;
